import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Users, DollarSign, PieChart, BarChart3 } from "lucide-react";
import { API_URL } from "../../components/config";

const BillingStats = () => {
  const [billingStats, setBillingStats] = useState({
    userPreferences: [],
    privateRideStats: [],
    rideshareStats: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        setLoading(true);
        
        // Fetch user billing preferences
        const userPrefsResponse = await fetch(`${API_URL}/users/billing-statistics`);
        const userPrefsData = await userPrefsResponse.json();
        
        // Fetch private ride billing stats (you'll need to create this endpoint)
        const privateRideResponse = await fetch(`${API_URL}/trip/billing-stats`);
        const privateRideData = await privateRideResponse.json();
        
        // Fetch rideshare billing stats
        const rideshareResponse = await fetch(`${API_URL}/rideshare/billing-stats`);
        const rideshareData = await rideshareResponse.json();
        
        setBillingStats({
          userPreferences: userPrefsData || [],
          privateRideStats: privateRideData || [],
          rideshareStats: rideshareData || []
        });
      } catch (error) {
        console.error("Error fetching billing stats:", error);
        // Set fallback data
        setBillingStats({
          userPreferences: [],
          privateRideStats: [],
          rideshareStats: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, []);

  const StatCard = ({ title, value, icon, color, subtitle, trend }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          {icon}
        </div>
        {trend && (
          <div className={`flex items-center text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            <TrendingUp className="w-4 h-4 mr-1" />
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <h3 className="text-2xl font-bold text-gray-800">{value}</h3>
      <p className="text-sm text-gray-500 mt-1">{title}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </motion.div>
  );

  const PreferenceCard = ({ preference, count, percentage }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-lg shadow-md p-4 border border-gray-100"
    >
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-lg font-semibold text-gray-800 capitalize">
            {preference}
          </h4>
          <p className="text-sm text-gray-500">{count} users</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-600">{percentage}%</div>
          <div className="text-xs text-gray-400">of total</div>
        </div>
      </div>
      <div className="mt-3">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    </motion.div>
  );

  const BillingTypeCard = ({ type, stats, icon }) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg p-6 border border-gray-100"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-800 capitalize">
          {type} Billing
        </h3>
        {icon}
      </div>
      
      {stats.length > 0 ? (
        <div className="space-y-3">
          {stats.map((stat, index) => (
            <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-700 capitalize">
                  {stat.billing_type}
                </p>
                <p className="text-xs text-gray-500">
                  {stat.trip_count || 0} trips
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-green-600">
                  ${Number(stat.total_billing || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">
                  Avg: ${Number(stat.avg_billing_per_trip || 0).toFixed(2)}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <PieChart className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>No billing data available</p>
        </div>
      )}
    </motion.div>
  );

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-xl shadow-lg p-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-300 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-300 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* User Billing Preferences */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
          <Users className="w-6 h-6 mr-2 text-blue-600" />
          User Billing Preferences
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {billingStats.userPreferences.length > 0 ? (
            billingStats.userPreferences.map((pref, index) => (
              <PreferenceCard
                key={index}
                preference={pref.billing_preference}
                count={pref.user_count}
                percentage={pref.percentage}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No billing preference data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Billing Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Private Ride Billing */}
        <BillingTypeCard
          type="Private Ride"
          stats={billingStats.privateRideStats}
          icon={<DollarSign className="w-5 h-5 text-green-600" />}
        />

        {/* Rideshare Billing */}
        <BillingTypeCard
          type="Rideshare"
          stats={billingStats.rideshareStats}
          icon={<BarChart3 className="w-5 h-5 text-purple-600" />}
        />
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Total Users with Preferences"
          value={billingStats.userPreferences.reduce((sum, pref) => sum + pref.user_count, 0)}
          icon={<Users className="w-5 h-5 text-blue-600" />}
          color="blue"
          subtitle="Active users"
        />
        <StatCard
          title="Percentage Users"
          value={`${billingStats.userPreferences.find(p => p.billing_preference === 'percentage')?.percentage || 0}%`}
          icon={<TrendingUp className="w-5 h-5 text-green-600" />}
          color="green"
          subtitle="Prefer percentage billing"
        />
        <StatCard
          title="Daily Users"
          value={`${billingStats.userPreferences.find(p => p.billing_preference === 'daily')?.percentage || 0}%`}
          icon={<PieChart className="w-5 h-5 text-purple-600" />}
          color="purple"
          subtitle="Prefer daily billing"
        />
      </div>
    </div>
  );
};

export default BillingStats;
